import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';
import {
    ActionLink,
    Block,
    Heading,
    Loader,
    IconName,
    Illustration,
} from '@klp/design';

import {
    ContractManagementRoutes,
    ContractManagementTabs,
    ContractRegistrationRoutes,
} from '@forvalter/routing/Routes';
import ContractRegistrationProcess from '../components/ContractRegistrationProcess';
import {
    useGetContractRegistrationProcessInstanceQuery,
    useGetContractToRegistrationQuery,
} from '../services/rtkApi';
import ChatView from '@klp/contractmanagement/components/ChatView';
import { Main } from '@klp/layout/Main';
import { ContractSummary } from '../components/ContractSummary';

const useStyles = makeStyles(() => ({
    chatWrapper: {
        width: '100%',
        position: 'relative',
    },
    notFoundContainer: {
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '60%',
    },
}));

export default function ContractRegistrationInstanceView() {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const { kontraktId, prosessInstansId } = useParams<{
        kontraktId: string;
        prosessInstansId: string;
    }>();

    const {
        data: contractToRegistration,
        isLoading: contractToRegistrationLoading,
        isFetching: contractToRegistrationFetching,
        isError: contractToRegistrationError,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const {
        data: registrationProcessData,
        isLoading: registrationProcessLoading,
        isFetching: registrationProcessFetching,
        isError: registrationProcessError,
    } = useGetContractRegistrationProcessInstanceQuery({
        processInstanceId: prosessInstansId,
        culture: i18n.language,
    });

    const isLoading =
        registrationProcessLoading ||
        registrationProcessFetching ||
        contractToRegistrationLoading ||
        contractToRegistrationFetching;

    if (isLoading) return <Loader isLoading={isLoading} />;

    if (
        registrationProcessError ||
        !registrationProcessData ||
        contractToRegistrationError ||
        !contractToRegistration
    ) {
        showToast({
            body: t('checkOffContract.error'),
            type: 'error',
        });
        return (
            <Main fullScreen={true}>
                <Block>
                    <ActionLink
                        actionType="linkBack"
                        translation="checkOffContract.backButton"
                        href={ContractManagementRoutes.tab(
                            ContractManagementTabs.registrering,
                        )}
                    />
                </Block>
                <Block
                    className={classes.notFoundContainer}
                    paddingTop="massive"
                >
                    <Illustration
                        paddingBottom="compact"
                        icon={IconName.CIRCLE_OBS}
                        size="xxxlarge"
                    />
                    <Heading
                        klpHeadingLevel="TWO"
                        klpColor="dark"
                        translation="checkOffContract.error"
                        data-cy="CheckOffContract__NotFound__Title"
                    />
                </Block>
            </Main>
        );
    }

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="contractManagement"
                    label={t('breadcrumbs.contractManagement')}
                    href={ContractManagementRoutes.moduleRoute()}
                />
                <Main.Crumb
                    id="checkOffContract"
                    label={contractToRegistration?.navn}
                />
            </Main.Breadcrumbs>
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="checkOffContract.backButton"
                    href={ContractManagementRoutes.tab(
                        ContractManagementTabs.registrering,
                    )}
                />
            </Main.Navigation>
            <div>
                <ChatView />
            </div>

            {contractToRegistration && (
                <ContractSummary contract={contractToRegistration} />
            )}
            {contractToRegistration.registreringProsessId !==
                prosessInstansId && (
                <Block>
                    <ActionLink
                        actionType="link"
                        translation="checkOffContract.registreringProsessLink"
                        translationOptions={{
                            type: t(
                                `contractRegistration.contractTypes.${contractToRegistration.registreringProsessType}`,
                            ),
                        }}
                        href={ContractRegistrationRoutes.single(kontraktId)}
                    />
                </Block>
            )}
            <ContractRegistrationProcess
                process={registrationProcessData}
                skalRegistreres={contractToRegistration.skalRegistreres}
            />
        </Main>
    );
}
